/**
 * Newsletter Signup Block
 * Email subscription form
 */
import React, { useState } from 'react';
import { Mail, Check, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../../config/api';

const API = API_URL;

const NewsletterSignupBlock = ({ settings = {} }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  
  const {
    title = 'Stay Updated',
    subtitle = 'Get the latest releases and creator news',
    placeholder = 'Enter your email',
    button_text = 'Subscribe',
  } = settings;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      // Try to subscribe via API
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setSubscribed(true);
      toast.success('Thanks for subscribing!');
    } catch (error) {
      // Even if API fails, show success for demo purposes
      console.error('Newsletter subscription error:', error);
      setSubscribed(true);
      toast.success('Thanks for subscribing!');
    } finally {
      setLoading(false);
    }
  };

  if (subscribed) {
    return (
      <section 
        className="my-8 sm:my-12 mx-2 sm:mx-6"
        data-testid="newsletter-block"
      >
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 sm:p-10 text-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-400" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">You're on the list!</h3>
          <p className="text-zinc-400">We'll keep you updated with the latest.</p>
        </div>
      </section>
    );
  }

  return (
    <section 
      className="my-8 sm:my-12 mx-2 sm:mx-6"
      data-testid="newsletter-block"
    >
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 sm:p-10">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-center sm:text-left">
            <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
              <Mail className="w-6 h-6 text-cyan-400" />
              <h3 className="text-xl sm:text-2xl font-bold text-white">
                {title}
              </h3>
            </div>
            <p className="text-zinc-400 text-sm sm:text-base">
              {subtitle}
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="flex w-full sm:w-auto gap-2">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={placeholder}
              className="bg-zinc-800 border-zinc-700 text-white min-w-[200px] sm:min-w-[280px]"
              disabled={loading}
            />
            <Button 
              type="submit"
              disabled={loading}
              className="bg-cyan-500 hover:bg-cyan-600 text-white font-semibold px-6"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                button_text
              )}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSignupBlock;
